import {
  companyOptions,
  companySubdivisionsOptions,
  deleteSubdivision,
  useCompany,
  useCompanySubdivisions,
} from "@/api";
import SubdivisionForm from "@/components/company_admin/subdivisions/SubdivisionForm";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import {
  firebaseTimestampToDate,
  getBuildingId,
  notifyApiError,
  NUMERIC_DATE_TIME_LOCALE,
  timestampSortingFn,
} from "@/lib";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { NotificationType, Subdivision } from "@/types";
import {
  Button,
  Chip,
  Icon,
  InputField,
  LoadingSpinner,
  PopupCard,
} from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";

export const Route = createFileRoute(
  "/_portal/company/$companyId/subdivisions",
)({
  component: ComponentWrapper,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;
    queryClient.ensureQueryData(companyOptions(companyId));
    queryClient.ensureQueryData(companySubdivisionsOptions(companyId, true));
  },
});

function ComponentWrapper() {
  return (
    <Suspense
      fallback={
        <div className="center">
          <LoadingSpinner />
        </div>
      }
    >
      <CompanySubdivisions />
    </Suspense>
  );
}

const columnHelper = createColumnHelper<Subdivision>();
const searchableColumns: Array<keyof Subdivision> = ["name", "orgNumber"];

function CompanySubdivisions() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { companyId } = Route.useParams();
  const { addNotification } = useSnackbarStore();
  const { isCompanyAdmin } = useCompanyAdminAccess(companyId);
  const { data: company } = useCompany(companyId);
  const { data: subdivisions, isLoading } = useCompanySubdivisions(
    companyId,
    true,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredSubdivisions, setFilteredSubdivisions] = useState<
    Subdivision[]
  >([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSubdivision, setEditingSubdivision] = useState<
    Subdivision | undefined
  >(undefined);

  const handleDeleteSubdivision = async (subdivision: Subdivision) => {
    try {
      if (subdivision.assignedUsersCount) return;

      await deleteSubdivision(subdivision.subdivisionId, subdivision.companyId);
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "company-subdivisions", companyId],
      });

      addNotification(
        t("NOTIFICATIONS.DOCUMENT_DELETED", {
          entity: `${t("SUBDIVISIONS.SUBDIVISION")} "${subdivision.name}"`,
        }),
        NotificationType.SUCCESS,
      );
    } catch (error) {
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: `${t("SUBDIVISIONS.SUBDIVISION")} "${subdivision.name}"`,
        },
        fallbackKey: "DELETE_FAILED",
      });
    }
  };

  useEffect(() => {
    if (searchTerm && !!subdivisions?.length) {
      const searchTerms = searchTerm.toLowerCase().split(" ");
      setFilteredSubdivisions(
        subdivisions.filter((subdivision: Subdivision) =>
          searchTerms.every((term) =>
            searchableColumns.some((column) => {
              const value = subdivision[column];
              return (value?.toString().toLowerCase() ?? "").includes(term);
            }),
          ),
        ),
      );
    } else {
      setFilteredSubdivisions(subdivisions ?? []);
    }
  }, [searchTerm, subdivisions]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader info={info} label={t("SUBDIVISIONS.NAME")} />
        ),
        meta: {
          label: t("SUBDIVISIONS.NAME"),
        },
      }),
      columnHelper.accessor("orgNumber", {
        cell: (info) => info.getValue() || "-",
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANY_SETTINGS.ORG_NUMBER")}
          />
        ),
        meta: {
          label: t("COMPANY_SETTINGS.ORG_NUMBER"),
        },
      }),
      columnHelper.accessor("invoiceReferences", {
        cell: (info) => {
          const references = info.getValue();
          if (!references || references.length === 0) return "-";
          const subdivisionId = info.row.original.subdivisionId;
          const referencesOverflow = references.slice(1);
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              <Chip label={references[0]} className="tableTag" />
              {referencesOverflow.length > 0 && (
                <>
                  <span
                    id={`references-overflow-${subdivisionId}`}
                    style={{ fontWeight: "bold", opacity: 0.5 }}
                  >{`+${referencesOverflow.length}`}</span>
                  <Tooltip
                    anchorSelect={`#references-overflow-${subdivisionId}`}
                    content={referencesOverflow.join(", ")}
                    style={{ maxWidth: 300 }}
                  />
                </>
              )}
            </div>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SUBDIVISIONS.INVOICE_REFERENCES")}
          />
        ),
        //sortingFn: arrayLengthSortingFn, // TODO: move this sorting fn in from welcome food branch
        meta: {
          label: t("SUBDIVISIONS.INVOICE_REFERENCES"),
        },
      }),
      columnHelper.accessor("assignedUsersCount", {
        cell: (info) => {
          const count = info.getValue();
          return (
            <>
              {count}
              {count === 0 && (
                <>
                  <Icon
                    name="warning"
                    className="orangeIcon"
                    style={{
                      verticalAlign: "middle",
                      marginLeft: "var(--wlcm-spacing-xs)",
                    }}
                  />
                  <Tooltip
                    anchorSelect=".orangeIcon"
                    content={t("SUBDIVISIONS.NO_ASSIGNED_USERS")}
                  />
                </>
              )}
            </>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SUBDIVISIONS.ASSIGNED_USERS_COUNT")}
          />
        ),
        meta: {
          label: t("SUBDIVISIONS.ASSIGNED_USERS_COUNT"),
        },
      }),
      columnHelper.accessor("createdAt", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            firebaseTimestampToDate(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("USERS.TABLE_HEADERS.CREATED_AT")}
          />
        ),
        sortingFn: timestampSortingFn,
        meta: {
          label: t("USERS.TABLE_HEADERS.CREATED_AT"),
        },
      }),
      columnHelper.accessor("updatedAt", {
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return "-";
          return NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            firebaseTimestampToDate(timestamp),
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMMUNICATION.UPDATED_AT")}
          />
        ),
        sortingFn: timestampSortingFn,
        meta: {
          label: t("COMMUNICATION.UPDATED_AT"),
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "actions",
        cell: (info) => {
          const subdivision = info.getValue();
          return (
            <>
              <div className="rowActions">
                <Icon
                  name="edit"
                  className={`edit-icon-${subdivision.subdivisionId}`}
                />
                {!subdivision.assignedUsersCount && (
                  <Icon
                    name="delete"
                    className={`delete-icon-${subdivision.subdivisionId}`}
                    style={{ color: "var(--wlcm-color-danger-red)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSubdivision(subdivision);
                    }}
                  />
                )}
              </div>
              <Tooltip
                anchorSelect={`.edit-icon-${subdivision.subdivisionId}`}
                content={t("EDIT")}
              />
              <Tooltip
                anchorSelect={`.delete-icon-${subdivision.subdivisionId}`}
                content={t("DELETE")}
              />
            </>
          );
        },
        header: () => (
          <div className="sr-only">{t("BUILDING_VISITORS.ACTIONS")}</div>
        ),
      }),
    ],
    [],
  );

  if (!isCompanyAdmin) return null;
  return (
    <>
      <div className="pageHeader">
        <h1>{t("SUBDIVISIONS.HEADER")}</h1>
        <p>
          {t("SUBDIVISIONS.SUBHEADER", {
            companyName: company.buildingTenantName,
          })}
        </p>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-md)",
          flexWrap: "wrap-reverse",
        }}
      >
        <InputField
          compact
          hideLabel
          label={t("SEARCH")}
          placeholder={t("SEARCH")}
          icon="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button icon="add" onClick={() => setModalOpen(true)}>
          {t("SUBDIVISIONS.ADD")}
        </Button>
      </div>
      <Table
        name="company-subdivisions"
        columns={columns}
        data={filteredSubdivisions}
        isLoading={isLoading}
        onRowClick={(subdivision) => {
          setEditingSubdivision(subdivision);
          setModalOpen(true);
        }}
        noDataMessage={t("SUBDIVISIONS.NO_SUBDIVISIONS")}
        withPagination
        enableColumnControls
      />
      <PopupCard
        onClose={() => {
          setModalOpen(false);
          setEditingSubdivision(undefined);
        }}
        open={modalOpen}
      >
        <SubdivisionForm
          subdivision={editingSubdivision}
          handleCloseModal={() => {
            setModalOpen(false);
            setEditingSubdivision(undefined);
          }}
        />
      </PopupCard>
    </>
  );
}
